/**
 * Build ordered sport selection cards with lightweight activity context.
 * Pure domain — overlay consumes this; no UI imports.
 */

import { listPlayableSportModules } from "../../sports/registry";
import { normalizeSportId } from "./sportTypes";
import { getSportSelectionPresentation } from "./getSportSelectionPresentation";

/**
 * @param {number} completedAtMs
 * @param {number} [nowMs]
 * @returns {string}
 */
export function formatSportPlayedRelative(completedAtMs, nowMs = Date.now()) {
  if (!completedAtMs) return "";
  const now = new Date(nowMs);
  const d = new Date(completedAtMs);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfToday - startOfDay) / 86400000);
  if (diffDays === 0) return "Played today";
  if (diffDays === 1) return "Played yesterday";
  if (diffDays < 7) return `Played ${diffDays} days ago`;
  if (diffDays < 14) return "Played last week";
  if (diffDays < 30) {
    const weeks = Math.max(2, Math.round(diffDays / 7));
    return `Played ${weeks} weeks ago`;
  }
  if (diffDays < 60) return "Played last month";
  const months = Math.max(2, Math.round(diffDays / 30));
  return `Played ${months} months ago`;
}

/**
 * @param {Array<{ sportId?: string, completedAt?: number, completedAtMs?: number, isWin?: boolean }>} cards
 */
function aggregateSportActivity(cards = []) {
  /** @type {Map<string, { lastPlayedAt: number, matchCount: number, streak: number }>} */
  const bySport = new Map();

  const sorted = [...cards].sort((a, b) => {
    const ta = Number(a.completedAtMs ?? a.completedAt) || 0;
    const tb = Number(b.completedAtMs ?? b.completedAt) || 0;
    return tb - ta;
  });

  for (const card of sorted) {
    const id = normalizeSportId(card.sportId);
    const at = Number(card.completedAtMs ?? card.completedAt) || 0;
    let row = bySport.get(id);
    if (!row) {
      row = { lastPlayedAt: at, matchCount: 0, streak: 0 };
      bySport.set(id, row);
    }
    row.matchCount += 1;
    if (at > row.lastPlayedAt) row.lastPlayedAt = at;
  }

  for (const [sportId, row] of bySport) {
    let streak = 0;
    for (const card of sorted) {
      if (normalizeSportId(card.sportId) !== sportId) continue;
      if (card.isWin === true) streak += 1;
      else break;
    }
    row.streak = streak;
  }

  return bySport;
}

/**
 * Prefer last-played context; fall back to career count / streak / first-match copy.
 * @param {{ lastPlayedAt?: number, matchCount?: number, streak?: number } | null} activity
 * @param {number} [nowMs]
 */
export function buildSportCardContextLine(activity, nowMs = Date.now()) {
  if (!activity || !activity.matchCount) return "Ready for your first match";
  if (activity.lastPlayedAt) {
    return formatSportPlayedRelative(activity.lastPlayedAt, nowMs);
  }
  if (activity.streak >= 3) {
    return `Current streak · ${activity.streak} wins`;
  }
  if (activity.matchCount > 0) {
    return `${activity.matchCount} career match${activity.matchCount === 1 ? "" : "es"}`;
  }
  return "Ready for your first match";
}

/**
 * @param {{
 *   activeSportId?: string | null,
 *   matchCards?: object[],
 *   modules?: object[],
 *   nowMs?: number,
 * }} [options]
 */
export function buildSportSelectionCards(options = {}) {
  const modules =
    options.modules?.length > 0 ? options.modules : listPlayableSportModules();
  const activeId = options.activeSportId
    ? normalizeSportId(options.activeSportId)
    : null;
  const nowMs = options.nowMs ?? Date.now();
  const activity = aggregateSportActivity(options.matchCards || []);

  const cards = modules.map((mod) => {
    const presentation = getSportSelectionPresentation(mod.id);
    const stats = activity.get(mod.id) || null;
    return {
      sportId: mod.id,
      displayName: presentation.displayName,
      emoji: presentation.emoji,
      icon: presentation.icon,
      glyph: presentation.glyph || presentation.icon,
      tagline: presentation.tagline,
      contextLine: buildSportCardContextLine(stats, nowMs),
      lastPlayedAt: stats?.lastPlayedAt || 0,
      matchCount: stats?.matchCount || 0,
      streak: stats?.streak || 0,
      isContinue: Boolean(activeId && mod.id === activeId),
    };
  });

  cards.sort((a, b) => {
    if (a.isContinue !== b.isContinue) return a.isContinue ? -1 : 1;
    if (a.lastPlayedAt !== b.lastPlayedAt) return b.lastPlayedAt - a.lastPlayedAt;
    if (a.matchCount !== b.matchCount) return b.matchCount - a.matchCount;
    return String(a.displayName).localeCompare(String(b.displayName));
  });

  return cards;
}

/**
 * Short entry splash line after confirming a sport.
 * @param {string} sportId
 */
export function getSportEntrySplashPhrase(sportId) {
  const id = normalizeSportId(sportId);
  const phrases = {
    badminton: "Let's play.",
    pickleball: "Game on.",
    tennis: "Your serve.",
    table_tennis: "Ready.",
  };
  return phrases[id] || "Let's play.";
}
